import { useEffect, useState } from 'react'
import { api, type ApiError, type CostSummaryDto } from '../../lib/api'
import { useAuthStore } from '../../lib/auth-store'
import { cn } from '../../lib/cn'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { StatusBox } from '../ui/badge'
import { FloatingInput } from '../ui/input'

/**
 * Cost summary — pulls Anthropic spend (last 30d + all time + top
 * projects), free-build credits, and Hetzner monthly run-rate into a
 * single read-only card. Read-shaped so every member can see; that's
 * the right default for cost transparency.
 */
export function CostsSection({ accessToken }: { accessToken: string | null }) {
  const tenantRole = useAuthStore((s) => s.tenant?.role)
  const canManage = tenantRole === 'owner' || tenantRole === 'admin'
  const [data, setData] = useState<CostSummaryDto | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [editingCap, setEditingCap] = useState(false)
  const [capDraft, setCapDraft] = useState('')
  const [savingCap, setSavingCap] = useState(false)

  useEffect(() => {
    if (!accessToken) return
    let cancelled = false
    api
      .getCosts(accessToken)
      .then((res) => {
        if (!cancelled) {
          setData(res)
          setErr(null)
          // Sync the cap input on reload — empty when unlimited.
          setCapDraft(
            res.anthropic.monthlyCapUsd === null
              ? ''
              : String(res.anthropic.monthlyCapUsd),
          )
        }
      })
      .catch((e: ApiError) => {
        if (!cancelled) setErr(e.title ?? 'Failed to load cost summary')
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, reloadKey])

  async function saveCap(value: number | null) {
    if (!accessToken) return
    setSavingCap(true)
    setErr(null)
    try {
      await api.updateSpendCap(accessToken, value)
      setEditingCap(false)
      setReloadKey((k) => k + 1)
    } catch (e) {
      const ae = e as ApiError
      setErr(ae.detail ?? ae.title ?? 'Failed to update cap')
    } finally {
      setSavingCap(false)
    }
  }

  if (err) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Workspace costs</CardTitle>
        </CardHeader>
        <CardContent>
          <StatusBox tone="destructive">{err}</StatusBox>
        </CardContent>
      </Card>
    )
  }
  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Workspace costs</CardTitle>
          <CardDescription>Loading cost summary…</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="dwlt-skeleton h-24" />
            <div className="dwlt-skeleton h-24" />
            <div className="dwlt-skeleton h-24" />
          </div>
        </CardContent>
      </Card>
    )
  }

  const usingManaged =
    !data.freeBuilds.unlimited && data.freeBuilds.managedAvailable
  const variableTotal30d = data.anthropic.last30dUsd
  const recurringMonthlyEur = data.hetzner.monthlyCostEur

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workspace costs</CardTitle>
        <CardDescription>
          What this workspace is spending. Anthropic is variable per build;
          Hetzner is recurring monthly per server.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-3">
          {/* Anthropic — variable. Surfaces cap state if one is set. */}
          <Card size="sm" className={cn(data.anthropic.capExceeded && 'ring-destructive/40 bg-destructive-soft')}>
            <CardContent className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Anthropic — last 30 days
              </p>
              <p className="text-2xl font-semibold tabular-nums">
                ${variableTotal30d.toFixed(2)}
                {data.anthropic.monthlyCapUsd !== null && (
                  <span className="ml-1 text-sm font-normal text-muted-foreground">
                    / ${data.anthropic.monthlyCapUsd.toFixed(2)}
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground tabular-nums">
                ${data.anthropic.allTimeUsd.toFixed(2)} all time
              </p>
              {data.anthropic.capExceeded && (
                <p className="pt-1 text-xs text-destructive">
                  Cap reached — new builds are blocked until spend rolls
                  off the 30-day window or an admin raises the cap.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Free credits — only meaningful for tenants on managed key */}
          <Card size="sm">
            <CardContent className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Free builds
              </p>
              {data.freeBuilds.unlimited ? (
                <p className="text-2xl font-semibold text-volt-ink">Unlimited</p>
              ) : usingManaged ? (
                <>
                  <p className="text-2xl font-semibold tabular-nums">
                    {data.freeBuilds.remaining}
                  </p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {data.freeBuilds.used} used · $
                    {parseFloat(data.freeBuilds.costUsd).toFixed(2)} on Doable
                  </p>
                </>
              ) : (
                <p className="pt-1 text-sm text-muted-foreground">
                  On your own Anthropic key.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Hetzner — recurring */}
          <Card size="sm">
            <CardContent className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Hetzner — recurring
              </p>
              <p className="text-2xl font-semibold tabular-nums">
                €{recurringMonthlyEur.toFixed(2)}
                <span className="text-sm font-normal text-muted-foreground"> /mo</span>
              </p>
              <p className="text-xs text-muted-foreground tabular-nums">
                {data.hetzner.serverCount} server
                {data.hetzner.serverCount === 1 ? '' : 's'} live
              </p>
            </CardContent>
          </Card>
        </div>

        {data.anthropic.topProjectsLast30d.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Top spenders — last 30 days
            </p>
            <ul className="divide-y rounded-xl border">
              {data.anthropic.topProjectsLast30d.map((p) => (
                <li
                  key={p.projectId}
                  className="flex items-center justify-between px-3 py-2 text-sm"
                >
                  <span className="truncate">{p.projectName}</span>
                  <span className="ml-3 shrink-0 text-muted-foreground tabular-nums">
                    ${p.costUsd.toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Hetzner billing goes directly from Hetzner to your account; we just
          show the cached price we recorded at provision time. Anthropic
          figures come from your build history.
        </p>
      </CardContent>

      {/* Spend-cap editor — admin only. Read-only display for everyone
          else so they understand why builds might refuse. */}
      {canManage ? (
        <CardFooter className="flex-col items-stretch gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-medium">Monthly Anthropic cap</p>
              <p className="text-xs text-muted-foreground">
                Hard limit on rolling 30-day spend. Builds refuse to start
                when this is reached. Set blank for unlimited.
              </p>
            </div>
            {!editingCap && (
              <Button variant="outline" size="sm" onClick={() => setEditingCap(true)}>
                Edit cap
              </Button>
            )}
          </div>
          {editingCap && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-44">
                <FloatingInput
                  label="Cap (USD, blank = unlimited)"
                  type="number"
                  min="0"
                  max="10000"
                  step="1"
                  value={capDraft}
                  onChange={(e) => setCapDraft(e.target.value)}
                  className="bg-card"
                />
              </div>
              <Button
                size="sm"
                disabled={savingCap}
                onClick={() => {
                  const trimmed = capDraft.trim()
                  const value =
                    trimmed === '' ? null : Math.min(10000, Math.max(0, Number(trimmed)))
                  saveCap(value)
                }}
              >
                {savingCap ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditingCap(false)
                  setCapDraft(
                    data.anthropic.monthlyCapUsd === null
                      ? ''
                      : String(data.anthropic.monthlyCapUsd),
                  )
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </CardFooter>
      ) : null}
    </Card>
  )
}
